import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { useAuth } from "@/providers/auth";
import { CreateInvoicePayload, CreateRecurringInvoicePayload } from "@/queries/invoice/types";
import { getContactName } from "@/utils/contact";
import { CreateInvoiceFormData } from "./schema";
import { format } from "date-fns";

interface InvoiceSummary {
  subtotal: number;
  taxAmount: number;
  total: number;
  getPayloadData: () => CreateInvoicePayload;
  getRecurringPayloadData: () => CreateRecurringInvoicePayload;
  mapFormToPreview: () => Record<string, string>;
}

export const useInvoiceSummary = (
  form: UseFormReturn<CreateInvoiceFormData>
): InvoiceSummary => {
  const { watch } = form;
  const { data: tenancy } = useTenancy();
  const { cognitoId, clientId, userRole } = useAuth();

  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const [lineItems, taxPercentage, taxAmount, shippingFee] = watch([
    "lineItems",
    "taxPercentage",
    "taxAmount",
    "shippingFee",
  ]);

  const subtotal = lineItems.reduce((sum, item) => {
    const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
    return sum + itemTotal;
  }, 0);

  const calculatedTaxAmount = useMemo(() => {
    const amount = taxAmount || (taxPercentage ? (subtotal * taxPercentage) / 100 : 0);
    return parseFloat(amount.toFixed(2));
  }, [taxAmount, taxPercentage, subtotal]);

  const total = useMemo(() => {
    const totalAmount = subtotal + calculatedTaxAmount + shippingFee;
    return parseFloat(totalAmount.toFixed(2));
  }, [subtotal, calculatedTaxAmount, shippingFee]);

  // Generate payload for invoice creation
  const getPayloadData = (): CreateInvoicePayload => {
    const formData = form.getValues();

    return {
      invoice_from: {
        business_id: (tenancy as any)?.business_id,
        user_id: userId,
      },
      invoice_to: formData.invoiceTo,
      invoice_frequency: formData.invoiceFrequency,
      invoice_frequency_interval: formData.invoiceFrequencyInterval,
      invoice_due_date: formData.dueDate?.toISOString() || null,
      invoice_send_date: formData.sendDate?.toISOString() || null,
      invoice_line_items: formData.lineItems,
      invoice_subtotal: subtotal,
      invoice_tax_percentage: !taxAmount ? formData.taxPercentage : null,
      invoice_tax_amount: calculatedTaxAmount,
      invoice_add_paypal: false,
      allow_credit_card_payment: formData.enableCardPayment,
      invoice_shipping_fee: formData.shippingFee,
      invoice_total: total,
      invoice_notes: formData.notes,
      invoice_delivery_method: formData.deliveryMethod,
      invoice_payment_terms: formData.terms || null,
      invoice_number: formData.invoiceNumber || null,
      invoice_external_id: formData.invoiceExternalId || null,
      invoice_external_id_link: formData.invoiceExternalIdLink || null,
    };
  };

  // Generate payload for recurring invoice creation
  const getRecurringPayloadData = (): CreateRecurringInvoicePayload => {
    const formData = form.getValues();

    return {
      interval_days: formData.recurringIntervalDays!,
      start_date: formData.startDate?.toISOString() || null,
      invoice_delivery_method: formData.deliveryMethod!,
      invoice_to: formData.invoiceTo,
      invoice_from: {
        business_id: (tenancy as any)?.business_id,
        user_id: userId,
      },
      invoice_line_items: formData.lineItems,
      invoice_subtotal: subtotal,
      invoice_tax_percentage: !taxAmount ? formData.taxPercentage : null,
      invoice_tax_amount: calculatedTaxAmount,
      invoice_pass_processing_fee: false,
      invoice_processing_fee: null,
      invoice_add_paypal: false,
      allow_credit_card_payment: formData.enableCardPayment,
      invoice_shipping_fee: formData.shippingFee,
      invoice_total: total,
      invoice_notes: formData.notes,
      invoice_payment_terms: formData.terms || null,
      invoice_number: formData.invoiceNumber || null,
    };
  };

  const mapFormToPreview = (): Record<string, string> => {
    const formData = form.getValues();

    // Utility to escape HTML for safe rendering
    const escapeHtml = (str: string | undefined | null): string => {
      if (!str) return "";
      return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    };

    return {
      customerName: formData.invoiceTo
        ? getContactName({
            contact_id: formData.invoiceTo.contact_id,
            contact_info: formData.invoiceTo,
          })
        : "",
      customerPhone: formData.invoiceTo?.phone || "",
      customerEmail: formData.invoiceTo?.email || "",
      customerAddress: formData.invoiceTo?.business_address || "",
      customerCityStateZip: formData.invoiceTo
        ? `${formData.invoiceTo.business_city || ""}${
            formData.invoiceTo.business_city ? "," : ""
          } ${formData.invoiceTo.business_state || ""} ${
            formData.invoiceTo.business_zip || ""
          }`.trim()
        : "",
      invoiceNumber: formData.invoiceNumber || "",
      invoiceDate: formData.sendDate
        ? format(formData.sendDate, "PPP")
        : format(new Date(), "PPP"),
      invoiceTerm: formData.terms || "N/A",
      dueDate: formData.dueDate ? format(formData.dueDate, "PPP") : "N/A",
      invoiceSubtotal: `$${subtotal.toFixed(2)}`,
      invoiceTax: `$${calculatedTaxAmount.toFixed(2)}`,
      invoiceShippingFee: `$${formData.shippingFee.toFixed(2)}`,
      invoiceTotal: `$${total.toFixed(2)}`,
      invoiceNotes: escapeHtml(formData.notes),
      lineItems: formData.lineItems
        .map(
          (item) =>
            `<tr><td>${escapeHtml(item.description)}</td><td>${
              item.quantity
            }</td><td>$${item.unit_price.toFixed(
              2
            )}</td><td>$${item.item_total.toFixed(2)}</td></tr>`
        )
        .join(""),
    };
  };

  return {
    subtotal,
    taxAmount: calculatedTaxAmount,
    total,
    getPayloadData,
    getRecurringPayloadData,
    mapFormToPreview,
  };
};
